import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRoleContext } from '../../hooks/useRoleContext';
import { useLanguage } from '../../context/LanguageContext';
import { Home, Mail, Phone, HelpCircle } from 'lucide-react';
import NewsletterSubscribe from '../NewsletterSubscribe';
import brand from '../../config/brand';
import { FOOTER_TEST_ID, resolveFooterSections } from '../../lib/footerNavigation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FacebookIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

export default function Footer() {
  const { t } = useLanguage();
  const { user, isAdmin } = useAuth();
  const roleContext = useRoleContext();
  const [socialStats, setSocialStats] = useState(null);

  const footerSections = resolveFooterSections({ user, isAdmin, viewAsRole: roleContext.viewAsRole });
  const sectionHrefs = footerSections.flatMap((section) => section.links.map((item) => item.href));
  const showContactFaq = !sectionHrefs.includes('/faq');

  useEffect(() => {
    fetchSocialStats();
  }, []);

  const fetchSocialStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/social-stats`);
      if (response.ok) {
        const data = await response.json();
        setSocialStats(data.social);
      }
    } catch (error) {
      console.error('Failed to fetch social stats:', error);
    }
  };

  const defaultSocialLinks = [
    { name: 'Facebook', icon: FacebookIcon, url: brand.social.facebook },
    { name: 'Instagram', icon: InstagramIcon, url: brand.social.instagram },
    { name: 'Twitter', icon: TwitterIcon, url: brand.social.twitter },
    { name: 'LinkedIn', icon: LinkedInIcon, url: brand.social.linkedin },
    { name: 'TikTok', icon: TikTokIcon, url: brand.social.tiktok },
  ];

  const socialLinks = defaultSocialLinks.map((link) => ({
    ...link,
    followers: socialStats?.[link.name.toLowerCase()]?.followers || 0,
    url: socialStats?.[link.name.toLowerCase()]?.url || link.url,
  }));

  return (
    <footer className="bg-gradient-to-br from-amber-50 via-stone-50 to-white border-t border-amber-100" data-testid={FOOTER_TEST_ID}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-0">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 pb-8">
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center shadow-sm">
                <Home className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold text-slate-800">
                {brand.brand_name_parts.prefix}{' '}
                <span className="text-amber-700">{brand.brand_name_parts.accent}</span>{' '}
                {brand.brand_name_parts.suffix}
              </span>
            </Link>
            <p className="text-slate-600 text-sm mb-4">
              {t('footer.tagline', 'Germany-first relocation housing for families, professionals, PCS and TLA travelers, and international movers building a life in Europe.')}
            </p>
            <div className="flex items-center gap-5 pt-4 border-t border-amber-200/70">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-500 hover:text-amber-700 transition-colors duration-200"
                  title={social.name}
                  aria-label={social.name}
                >
                  <social.icon />
                </a>
              ))}
            </div>
          </div>

          {footerSections.map((section) => (
            <div key={section.id} data-testid={`footer-section-${section.id}`}>
              <h4 className="font-heading text-sm font-bold text-slate-800 uppercase tracking-wider mb-4">
                {t(section.titleKey, section.defaultTitle)}
              </h4>
              <ul className="space-y-2">
                {section.links.map((item) => (
                  <li key={item.testId}>
                    <Link
                      to={item.href}
                      className="text-sm text-slate-600 hover:text-amber-700 transition-colors"
                      data-testid={item.testId}
                    >
                      {t(item.labelKey, item.defaultLabel)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div data-testid="footer-section-contact">
            <h4 className="font-heading text-sm font-bold text-slate-800 uppercase tracking-wider mb-4">
              {t('footer.contact_us', 'Contact Us')}
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${brand.contact.info}`}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-amber-700 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  {brand.contact.info}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${brand.contact.phone_href}`}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-amber-700 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {brand.contact.phone}
                </a>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-amber-700 transition-colors"
                  data-testid="footer-contact-form"
                >
                  <HelpCircle className="w-4 h-4" />
                  {t('footer.contact_form', 'Contact Form')}
                </Link>
              </li>
              {showContactFaq && (
                <li>
                  <Link
                    to="/faq"
                    className="text-sm text-slate-600 hover:text-amber-700 transition-colors"
                    data-testid="footer-contact-faq"
                  >
                    {t('footer.faq_support', 'FAQ & Support')}
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-amber-200/70 py-8 text-center">
          <p className="text-sm font-semibold text-slate-800 mb-1">
            {t('newsletter.title', 'Stay Updated')}
          </p>
          <p className="text-xs text-slate-500 mb-4">
            {t('newsletter.subtitle', 'Get the latest relocation-ready homes and practical moving guidance')}
          </p>
          <div className="max-w-sm mx-auto">
            <NewsletterSubscribe variant="compact" source="footer" />
          </div>
        </div>

        <div className="border-t border-amber-200/70 py-6">
          <p className="text-xs text-slate-500 leading-relaxed text-center max-w-4xl mx-auto" data-testid="footer-non-affiliation">
            {t(
              'footer.non_affiliation',
              'TLA Housing Europe is an independent private platform. We are not affiliated with, endorsed by, or operated by the U.S. Government, the Department of Defense, any military branch, or any housing office or government agency. TLA eligibility and entitlement decisions are determined solely by your command or housing office, not by this platform.',
            )}
          </p>
        </div>

        <div className="border-t border-amber-200/70 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} {brand.copyright_entity}. {t('footer.rights_reserved', 'All rights reserved.')}
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <Link to="/privacy" className="hover:text-amber-700 transition-colors" data-testid="footer-bottom-privacy">
              {t('footer.privacy', 'Privacy Policy')}
            </Link>
            <Link to="/terms" className="hover:text-amber-700 transition-colors" data-testid="footer-bottom-terms">
              {t('footer.terms', 'Terms of Service')}
            </Link>
            <Link to="/imprint" className="hover:text-amber-700 transition-colors" data-testid="footer-bottom-imprint">
              {t('footer.imprint', 'Imprint')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
